// frontend/src/pages/PublicPlayerStatsPage.tsx
//
// Public player-stats browser. Pick a league (pill) → see the FULL season's
// player pool with instant, client-side sort / filter / search (no 50-cap,
// no "Load More"). The whole season is fetched once and everything after is
// done in the browser.
//
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiCompetitionSlim, PlayerRead } from '@/api/schemas';
import { fetchAllCompetitions } from '@/api/dfs';
import { fetchHistoricalPlayerStats, fetchPlayersStatsForCompetitionActiveSeason, fetchResearchSeasons } from '@/api/stats';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import { trackPlayerResearch } from '@/utils/research';
import { foldForSearch, formatPlayerDisplayName } from '@/utils/formatting';
import { pairSeasonSlotsAcrossComps, seasonPillLabel, sortSeasonsChronologically } from '@/utils/seasons';
import { COMBINED_COMP_ID, mergeAndRerankCombined } from '@/utils/combinedStats';
import { compareNullableRank, normalizedRankCompetitionIds } from '@/utils/playerRank';

import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import SEO from '@/components/common/SEO';
import Spinner from '@/components/common/Spinner';
import PlayerStatsTable, { transformStatsToPlayerRead } from '@/components/common/PlayerStatsTable';
import { userFacingError } from '@/utils/userFacingError';
import ClubResearchBrowser from '@/components/common/ClubResearchBrowser';
import ResearchViewToggle from '@/components/common/ResearchViewToggle';
import { supportedResearchCompetitions, type ResearchView } from '@/utils/clubResearch';

export type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

const PublicPlayerStatsPage: React.FC = () => {
  const { user, token } = useAuth();
  const [researchView, setResearchView] = useState<ResearchView>('players');

  const [competitions, setCompetitions] = useState<ApiCompetitionSlim[]>([]);
  const [isLoadingCompetitions, setIsLoadingCompetitions] = useState(true);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string>('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
    sortBy: 'fantasy_points',
    sortOrder: 'desc',
  });
  const [positionFilter, setPositionFilter] = useState<PositionFilter>('ALL');
  const [teamFilter, setTeamFilter] = useState<string>('ALL');
  const [selectedSeasonIdentifier, setSelectedSeasonIdentifier] = useState<string>('');

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<PlayerRead | null>(null);
  const [modalCompetitionContext, setModalCompetitionContext] = useState<{ id: number | null; name: string | null } | null>(null);

  const isCombined = selectedCompetitionId === COMBINED_COMP_ID;
  const numericCompetitionId = useMemo(
    () => (selectedCompetitionId && !isCombined ? parseInt(selectedCompetitionId, 10) : null),
    [selectedCompetitionId, isCombined]
  );
  const hasCompSelection = isCombined || !!numericCompetitionId;

  // Debounce search (client-side, so a short delay is plenty).
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 200);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Conviction signal (STRATEGY C-ENG2): visiting the stats table is app-level research.
  useEffect(() => {
    trackPlayerResearch('stats_page_view');
  }, []);

  // Conviction signal: a settled, non-empty search is a deliberate player lookup.
  useEffect(() => {
    const term = debouncedSearchTerm.trim();
    if (term) trackPlayerResearch('stats_search', { 'Term': term });
  }, [debouncedSearchTerm]);

  // Load competitions for the league pills; default to last selection → EPL → first.
  useEffect(() => {
    let cancelled = false;
    setIsLoadingCompetitions(true);
    fetchAllCompetitions(true)
      .then((comps) => {
        if (cancelled) return;
        // EPL first (left), La Liga second, anything else after.
        const rank = (c: ApiCompetitionSlim) => {
          const s = (c.short_name || '').toUpperCase();
          const n = (c.name || '').toLowerCase();
          if (s === 'EPL' || n.includes('premier')) return 0;
          if (s === 'LA LIGA' || n.includes('la liga')) return 1;
          return 2;
        };
        const sorted = [...comps].sort((a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || ''));
        setCompetitions(sorted);
        if (sorted.length > 0) {
          const stored = typeof window !== 'undefined' ? window.localStorage.getItem('publicStats.competitionId') : null;
          if (stored === COMBINED_COMP_ID && sorted.length > 1) {
            setSelectedCompetitionId(COMBINED_COMP_ID);
          } else {
            const storedComp = stored ? sorted.find((c) => String(c.id) === stored) : undefined;
            if (storedComp) {
              setSelectedCompetitionId(String(storedComp.id));
            } else if (sorted.length > 1) {
              // No remembered choice: the combined view is the default.
              setSelectedCompetitionId(COMBINED_COMP_ID);
            } else {
              const epl = sorted.find((c) => c.short_name === 'EPL' || c.name === 'Premier League');
              setSelectedCompetitionId(String((epl ?? sorted[0]).id));
            }
          }
        }
      })
      .catch(() => { /* pills just won't render; table shows loading */ })
      .finally(() => { if (!cancelled) setIsLoadingCompetitions(false); });
    return () => { cancelled = true; };
  }, []);

  // Current + most recent archived season, ordered oldest -> newest so the
  // most recent season sits rightmost and is the default selection (owner
  // call 2026-07-16). Pills are labeled with concrete years, and preseason
  // the current season serves last season's numbers via the /stats fallback.
  const {
    data: seasonData,
    isLoading: isLoadingSeasons,
    error: seasonsError,
  } = useQuery({
    queryKey: ['researchSeasons', selectedCompetitionId],
    queryFn: async () => {
      const comps = isCombined
        ? competitions
        : competitions.filter((c) => c.id === numericCompetitionId);
      const lists = await Promise.all(comps.map((c) => fetchResearchSeasons(c.id)));
      return pairSeasonSlotsAcrossComps(comps, lists);
    },
    enabled: hasCompSelection && competitions.length > 0,
    staleTime: 10 * 60 * 1000,
  });
  // ALL seasons the backend knows about (current + every archived snapshot),
  // oldest -> newest so the current season is the rightmost pill.
  const researchSeasons = useMemo(
    () => sortSeasonsChronologically(seasonData?.options ?? []),
    [seasonData],
  );

  useEffect(() => {
    if (!researchSeasons.length) return;
    if (researchSeasons.some(option => option.season_identifier === selectedSeasonIdentifier)) return;
    // Default = the most recent season that actually HAS data (owner
    // 2026-07-23). Preseason the current-season pill is empty and this page
    // requests it with fallback_to_prior=false, so defaulting to it rendered
    // a full table of zeros. This runs only when no valid season is selected
    // yet, so it never yanks a season the user deliberately picked: an
    // already-open page keeps its selection and picks up the new season on
    // the next load, once GW1 is final.
    const withData = researchSeasons.filter(option => option.has_data);
    const defaultOption =
      (withData.length > 0 ? withData[withData.length - 1] : null)
      ?? researchSeasons.find(option => option.is_current)
      ?? researchSeasons[researchSeasons.length - 1];
    setSelectedSeasonIdentifier(defaultOption.season_identifier);
  }, [researchSeasons, selectedSeasonIdentifier]);

  const selectedSeason = useMemo(
    () => researchSeasons.find(option => option.season_identifier === selectedSeasonIdentifier) ?? null,
    [researchSeasons, selectedSeasonIdentifier],
  );

  // Load the WHOLE season once; sort/filter/search are all client-side after
  // this. Combined mode fetches every comp's slice of the season slot and
  // merges client-side - scoring is cross-comp comparable, so no backend
  // endpoint is needed.
  const seasonParts = seasonData?.partsByIdent?.[selectedSeasonIdentifier] ?? [];
  const { data: rawData, isLoading: isLoadingPlayers, error: playersError } = useQuery({
    queryKey: ['publicPlayerStatsAll', selectedCompetitionId, selectedSeasonIdentifier],
    queryFn: async () => {
      if (!selectedSeason || seasonParts.length === 0) return [] as any[];
      const perComp = await Promise.all(seasonParts.map(async (part) => {
        const rows = part.is_current
          ? await fetchPlayersStatsForCompetitionActiveSeason(
              part.compId, { limit: 1500, fallback_to_prior: false }, token)
          : await fetchHistoricalPlayerStats(
              part.compId, part.ident, { limit: 1500 }, token);
        // Tag provenance so the profile modal opens in the player's OWN
        // competition context (combined rows span two leagues).
        return (rows ?? []).map((r: any) => ({ ...r, __srcCompId: part.compId, __srcCompName: part.compName }));
      }));
      const rows = perComp.flat();
      // Current-season table ranks must match the live profile rule even for
      // negative-point players. Historical single-comp rows keep their stored
      // final ranks; combined rows always need a pooled recompute.
      return seasonParts.length > 1 || seasonParts[0]?.is_current
        ? mergeAndRerankCombined(rows) : rows;
    },
    enabled: hasCompSelection && !!selectedSeason && seasonParts.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const allPlayers = useMemo(() => (rawData ?? []).map(transformStatsToPlayerRead), [rawData]);

  const teamOptions = useMemo(() => Array.from(new Set(
    allPlayers.map(player => player.current_team_name).filter((name): name is string => !!name)
  )).sort((a, b) => a.localeCompare(b)), [allPlayers]);

  // Client-side filter + search + sort over the full pool — instant, no refetch.
  const displayPlayers = useMemo(() => {
    let list = allPlayers;
    if (positionFilter !== 'ALL') list = list.filter((p) => p.primary_position === positionFilter);
    if (teamFilter !== 'ALL') {
      list = list.filter((p) => p.current_team_name === teamFilter);
    }
    const term = foldForSearch(debouncedSearchTerm.trim());
    if (term) {
      // foldForSearch both sides: "baena" must match "Báena", "alvarez" -> "Álvarez".
      list = list.filter(
        (p) => foldForSearch(formatPlayerDisplayName(p)).includes(term) || foldForSearch(p.name).includes(term)
      );
    }
    const { sortBy, sortOrder } = sortConfig;
    const dir = sortOrder === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortBy === 'name') {
        return dir * formatPlayerDisplayName(a).localeCompare(formatPlayerDisplayName(b));
      }
      const av = (a as any)[sortBy];
      const bv = (b as any)[sortBy];
      if (sortBy === 'rank' || sortBy === 'position_rank') {
        return compareNullableRank(av, bv, dir);
      }
      const an = Number(av);
      const bn = Number(bv);
      // Numeric columns compare numerically; string columns (Pos, Team) by locale.
      if (Number.isFinite(an) && Number.isFinite(bn)) return dir * (an - bn);
      return dir * String(av ?? '').localeCompare(String(bv ?? ''));
    });
  }, [allPlayers, positionFilter, teamFilter, debouncedSearchTerm, sortConfig]);

  const handlePlayerClick = useCallback((player: PlayerRead) => {
    // Rows carry their source comp (combined mode spans leagues); fall back
    // to the browsed comp for single-league mode.
    const srcId = (player as any).__srcCompId ?? numericCompetitionId;
    const srcName = (player as any).__srcCompName
      ?? competitions.find((c) => String(c.id) === selectedCompetitionId)?.name
      ?? null;
    if (srcId == null) return;
    setSelectedPlayerForModal(player);
    setModalCompetitionContext({ id: srcId, name: srcName });
    setIsProfileModalOpen(true);
    mixpanelTrack('Viewed Player Profile Modal', { Source: 'Player Stats Page', 'Player ID': player.id, 'User ID': user?.id });
  }, [competitions, selectedCompetitionId, numericCompetitionId, user?.id]);

  const handleSortChange = (sortBy: string) => {
    trackPlayerResearch('stats_sort', { 'Column': sortBy });
    setSortConfig((prev) =>
      prev.sortBy === sortBy
        ? { sortBy, sortOrder: prev.sortOrder === 'desc' ? 'asc' : 'desc' }
        : { sortBy, sortOrder: 'desc' }
    );
  };

  const pageError = [playersError, seasonsError].find(error => error instanceof Error) as Error | null;
  const showSpinner = !hasCompSelection || isLoadingSeasons || (isLoadingPlayers && allPlayers.length === 0);

  // text-base (16px) on mobile so iOS Safari doesn't auto-zoom on focus; py-2.5
  // gives a ~44px touch target. Both tighten back down on sm+ (desktop).
  const inputClass = 'px-3 py-2.5 sm:py-1.5 bg-primary border border-gray-600 rounded text-base sm:text-sm text-gray-200 focus:outline-none focus:border-accent-yellow';

  return (
    <>
      <SEO
        title="Premier League & La Liga Player and Team Stats | Fantasy Futbol"
        description="Research Premier League and La Liga players and teams: fantasy points, power rankings, offense, defense, and opponent ranks."
        path="/stats"
      />

      <PlayerProfileModal
        isOpen={isProfileModalOpen}
        rankCompetitionIds={normalizedRankCompetitionIds(isCombined ? competitions.map(comp => comp.id) : numericCompetitionId ? [numericCompetitionId] : [])}
        player={selectedPlayerForModal}
        onClose={() => { setIsProfileModalOpen(false); setSelectedPlayerForModal(null); setModalCompetitionContext(null); }}
        competitionId={modalCompetitionContext?.id ?? null}
        competitionName={modalCompetitionContext?.name ?? null}
        pinCompetition
        isMyPlayer={false}
        onDropPlayer={() => {}}
        onAddPlayer={() => {}}
        onTradePlayer={() => {}}
        isActionLoading={false}
      />

      <div className="min-h-screen bg-primary px-3 sm:px-4 pt-3 pb-20 text-gray-200 font-body">
        <h1 className="sr-only">Premier League and La Liga Player and Team Stats</h1>

        <div className="max-w-7xl mx-auto">
          <ResearchViewToggle value={researchView} onChange={setResearchView} />
          <ClubResearchBrowser competitionIds={supportedResearchCompetitions(competitions).map(comp => comp.id)} enabled={researchView === 'teams'} />
          <div hidden={researchView !== 'players'}>
          {/* League pills */}
          <div className="flex items-center justify-center gap-2 mb-3 overflow-x-auto scrollbar-mobile-hide" role="tablist" aria-label="League">
            {isLoadingCompetitions && competitions.length === 0 && (
              <span className="text-sm text-gray-400">Loading leagues…</span>
            )}
            {(competitions.length > 1
              ? [{ pillId: COMBINED_COMP_ID, label: competitions.map((c) => c.short_name || c.name).join(' + ') }]
              : []
            ).concat(competitions.map((c) => ({ pillId: String(c.id), label: c.short_name || c.name || '' })))
              .map(({ pillId, label }) => {
              const active = pillId === selectedCompetitionId;
              return (
                <button
                  key={pillId}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setSelectedCompetitionId(pillId);
                    setSelectedSeasonIdentifier('');
                    // Reset all filters/sorts back to default when switching leagues.
                    setSortConfig({ sortBy: 'fantasy_points', sortOrder: 'desc' });
                    setPositionFilter('ALL');
                    setTeamFilter('ALL');
                    setSearchTerm('');
                    setDebouncedSearchTerm('');
                    try { window.localStorage.setItem('publicStats.competitionId', pillId); } catch { /* ignore */ }
                  }}
                  className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
                    active ? 'bg-accent-yellow text-primary' : 'bg-primary-dark text-gray-300 hover:text-white border border-gray-700'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Research season is explicit: each pill is a concrete season, so the
              current season never silently contains prior-season data. */}
          {researchSeasons.length > 0 && (
            <div className="flex items-center justify-center gap-2 mb-3 overflow-x-auto scrollbar-mobile-hide" role="tablist" aria-label="Stats season">
              {researchSeasons.map((option) => {
                const active = option.season_identifier === selectedSeasonIdentifier;
                return (
                  <button
                    key={option.season_identifier}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      setSelectedSeasonIdentifier(option.season_identifier);
                      setTeamFilter('ALL');
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${
                      active
                        ? 'bg-accent-yellow text-primary border-accent-yellow'
                        : 'bg-primary-dark text-gray-300 hover:text-white border-gray-700'
                    }`}
                  >
                    {seasonPillLabel(option.season_identifier)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Filter toolbar */}
          <div className="bg-primary-dark border border-gray-700 rounded-t-lg px-3 py-2">
            <div className="flex flex-col lg:flex-row gap-2">
              <input
                type="text"
                placeholder="Search players…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`flex-1 ${inputClass} placeholder-gray-500`}
                aria-label="Search players"
              />
              <div className="flex gap-2">
                <select value={positionFilter} onChange={(e) => { trackPlayerResearch('stats_filter', { 'Filter': 'position', 'Value': e.target.value }); setPositionFilter(e.target.value as PositionFilter); }} className={`flex-1 lg:flex-none ${inputClass}`} aria-label="Filter by position">
                  <option value="ALL">All Positions</option>
                  <option value="FW">FW</option>
                  <option value="MF">MF</option>
                  <option value="DF">DF</option>
                  <option value="GK">GK</option>
                </select>
                <select value={teamFilter} onChange={(e) => { trackPlayerResearch('stats_filter', { 'Filter': 'team', 'Value': e.target.value }); setTeamFilter(e.target.value); }} className={`flex-1 lg:flex-none ${inputClass}`} aria-label="Filter by team">
                  <option value="ALL">All Teams</option>
                  {teamOptions.map((teamName) => (
                    <option key={teamName} value={teamName}>{teamName}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-primary-dark rounded-b-lg border-x border-b border-gray-700 overflow-hidden">
            {showSpinner ? (
              <div className="flex justify-center items-center py-20">
                <Spinner size="lg" />
                <span className="ml-3 text-gray-400">Loading players…</span>
              </div>
            ) : pageError ? (
              <div className="text-center p-10 text-secondary bg-red-900/30">
                <p className="font-semibold text-lg">Could not load players</p>
                <p>{userFacingError(pageError, 'An unknown error occurred.')}</p>
              </div>
            ) : (
              <PlayerStatsTable
                players={displayPlayers}
                isLoading={isLoadingPlayers}
                error={null}
                onPlayerRowClick={handlePlayerClick}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
              />
            )}
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicPlayerStatsPage;
